import { useState, useEffect, useCallback } from 'react';
import folderService from '../services/folderService';
import { notify } from '../components/CustomToast';
import { sanitizeUrl } from '../utils/url';

export const useFileManager = () => {
    const [rootFolders, setRootFolders] = useState({ owned: [], shared: [], sharedFiles: [] });
    const [currentFolder, setCurrentFolder] = useState(null);
    const [folderContents, setFolderContents] = useState({ subfolders: [], files: [] });
    const [breadcrumb, setBreadcrumb] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [isAtRoot, setIsAtRoot] = useState(false);

    // Fetch root folders
    const fetchRootFolders = useCallback(async () => {
        try {
            setLoading(true);
            setIsAtRoot(true);
            const response = await folderService.getRootFolders();
            if (response?.status && response?.data) {
                setRootFolders({
                    owned: response.data.owned || [],
                    shared: response.data.shared || [],
                    sharedFiles: response.data.shared_files || [],
                });
            }
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to fetch folders');
            notify('error', 'Failed to load folders');
        } finally {
            setLoading(false);
        }
    }, []);

    // Initialize file manager - get home folder and navigate to it
    const initFileManager = useCallback(async () => {
        try {
            setLoading(true);
            setIsAtRoot(false);
            const response = await folderService.getHomeFolder();
            if (response?.status && response?.data?.folder) {
                const homeFolder = response.data.folder;
                setCurrentFolder(homeFolder);

                // Fetch contents of home folder
                const contentsResponse = await folderService.getFolderContents(homeFolder.id);
                if (contentsResponse?.status && contentsResponse?.data) {
                    setFolderContents({
                        subfolders: contentsResponse.data.subfolders || [],
                        files: contentsResponse.data.files || [],
                    });
                    setBreadcrumb(contentsResponse.data.breadcrumb || []);
                }
            }
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to initialize file manager');
            notify('error', 'Failed to load file manager');
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch folder contents
    const fetchFolderContents = useCallback(async (folderId) => {
        try {
            setLoading(true);
            setIsAtRoot(false);
            const response = await folderService.getFolderContents(folderId);
            if (response?.status && response?.data) {
                setCurrentFolder(response.data.folder);
                setFolderContents({
                    subfolders: response.data.subfolders || [],
                    files: response.data.files || [],
                });
                setBreadcrumb(response.data.breadcrumb || []);
            }
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to fetch folder contents');
            notify('error', 'Failed to load folder contents');
        } finally {
            setLoading(false);
        }
    }, []);

    // Navigate to folder
    const navigateToFolder = useCallback(async (folderId) => {
        if (folderId === null) {
            // Go to root
            setCurrentFolder(null);
            setFolderContents({ subfolders: [], files: [] });
            setBreadcrumb([]);
            await fetchRootFolders();
        } else {
            await fetchFolderContents(folderId);
        }
    }, [fetchRootFolders, fetchFolderContents]);

    // Create folder
    const createFolder = async (name, parentId = null) => {
        try {
            setIsProcessing(true);
            const response = await folderService.createFolder({ name, parent_id: parentId });
            notify('success', 'Folder created successfully');
            if (currentFolder) {
                await fetchFolderContents(currentFolder.id);
            } else {
                await fetchRootFolders();
            }
            return response?.data?.folder;
        } catch (err) {
            notify('error', err.message || 'Failed to create folder');
            throw err;
        } finally {
            setIsProcessing(false);
        }
    };

    // Rename folder
    const renameFolder = async (folderId, newName) => {
        try {
            setIsProcessing(true);
            const response = await folderService.updateFolder(folderId, { name: newName });
            notify('success', 'Folder renamed successfully');
            if (currentFolder) {
                await fetchFolderContents(currentFolder.id);
            } else {
                await fetchRootFolders();
            }
            return response?.data?.folder;
        } catch (err) {
            notify('error', err.message || 'Failed to rename folder');
            throw err;
        } finally {
            setIsProcessing(false);
        }
    };



    // Delete folder
    const deleteFolder = async (folderId) => {
        try {
            setIsProcessing(true);
            await folderService.deleteFolder(folderId);
            notify('delete', 'Folder deleted successfully');
            if (currentFolder) {
                await fetchFolderContents(currentFolder.id);
            } else {
                await fetchRootFolders();
            }
            return true;
        } catch (err) {
            notify('error', err.message || 'Failed to delete folder');
            throw err;
        } finally {
            setIsProcessing(false);
        }
    };

    // Move folder
    const moveFolder = async (folderId, newParentId) => {
        try {
            setIsProcessing(true);
            const response = await folderService.moveFolder(folderId, newParentId);
            notify('success', 'Folder moved successfully');
            if (currentFolder) {
                await fetchFolderContents(currentFolder.id);
            } else {
                await fetchRootFolders();
            }
            return response?.data;
        } catch (err) {
            notify('error', err.message || 'Failed to move folder');
            throw err;
        } finally {
            setIsProcessing(false);
        }
    };

    // Upload file
    const uploadFile = async (folderId, file) => {
        try {
            setIsProcessing(true);
            setUploadProgress(0);
            const response = await folderService.uploadFile(folderId, file, (progress) => {
                setUploadProgress(progress);
            });
            setUploadProgress(null);
            notify('success', 'File uploaded successfully');
            if (currentFolder) {
                await fetchFolderContents(currentFolder.id);
            }
            return response?.data?.file;
        } catch (err) {
            setUploadProgress(null);
            notify('error', err.message || 'Failed to upload file');
            throw err;
        } finally {
            setIsProcessing(false);
        }
    };

    // Delete file
    const deleteFile = async (fileId) => {
        try {
            setIsProcessing(true);
            await folderService.deleteFile(fileId);
            notify('delete', 'File deleted successfully');
            if (currentFolder) {
                await fetchFolderContents(currentFolder.id);
            }
            return true;
        } catch (err) {
            notify('error', err.message || 'Failed to delete file');
            throw err;
        } finally {
            setIsProcessing(false);
        }
    };

    // Open file in browser
    const openFile = async (fileId) => {
        try {
            setIsProcessing(true);
            const response = await folderService.getFileViewUrl(fileId);
            if (response?.status && response?.data?.url) {
                const fixedUrl = sanitizeUrl(response.data.url);
                window.open(fixedUrl, '_blank');
            }
        } catch (err) {
            notify('error', err.message || 'Failed to open file');
            throw err;
        } finally {
            setIsProcessing(false);
        }
    };

    // Share folder
    const shareFolder = async (folderId, userIds, permission = 'view') => {
        try {
            const response = await folderService.shareFolder(folderId, { user_ids: userIds, permission });
            notify('success', 'Folder shared successfully');

            // Refresh current view
            if (currentFolder) {
                await fetchFolderContents(currentFolder.id);
            } else {
                await fetchRootFolders();
            }

            return response?.data?.folder;
        } catch (err) {
            notify('error', err.message || 'Failed to share folder');
            throw err;
        }
    };

    // Remove folder share
    const removeFolderShare = async (folderId, userId) => {
        try {
            const response = await folderService.removeFolderShare(folderId, userId);
            notify('delete', 'Share removed successfully');

            // Refresh current view
            if (currentFolder) {
                await fetchFolderContents(currentFolder.id);
            } else {
                await fetchRootFolders();
            }

            return response?.data?.folder;
        } catch (err) {
            notify('error', err.message || 'Failed to remove share');
            throw err;
        }
    };

    // Share file
    const shareFile = async (fileId, userIds, permission = 'view') => {
        try {
            const response = await folderService.shareFile(fileId, { user_ids: userIds, permission });
            notify('success', 'File shared successfully');

            // Refresh current view
            if (currentFolder) {
                await fetchFolderContents(currentFolder.id);
            }

            return response?.data?.file;
        } catch (err) {
            notify('error', err.message || 'Failed to share file');
            throw err;
        }
    };

    // Remove file share
    const removeFileShare = async (fileId, userId) => {
        try {
            const response = await folderService.removeFileShare(fileId, userId);
            notify('success', 'Share removed successfully');

            // Refresh current view
            if (currentFolder) {
                await fetchFolderContents(currentFolder.id);
            }

            return response?.data?.file;
        } catch (err) {
            notify('error', err.message || 'Failed to remove share');
            throw err;
        }
    };

    // Move file
    const moveFile = async (fileId, newFolderId) => {
        try {
            setIsProcessing(true);
            const response = await folderService.moveFile(fileId, newFolderId);
            notify('success', 'File moved successfully'); // Changed back from my mistake in Turn 234

            // Refresh current view
            if (currentFolder) {
                await fetchFolderContents(currentFolder.id);
            }

            return response?.data;
        } catch (err) {
            notify('error', err.message || 'Failed to move file');
            throw err;
        } finally {
            setIsProcessing(false);
        }
    };

    // Search
    const search = async (query) => {
        try {
            setLoading(true);
            const response = await folderService.search(query);
            if (response?.status && response?.data) {
                return {
                    folders: response.data.folders || [],
                    files: response.data.files || [],
                };
            }
            return { folders: [], files: [] };
        } catch (err) {
            notify('error', err.message || 'Search failed');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Initial load - navigate to home folder
    useEffect(() => {
        initFileManager();
    }, [initFileManager]);

    return {
        // State
        rootFolders,
        currentFolder,
        folderContents,
        breadcrumb,
        loading,
        isProcessing,
        isAtRoot,
        error,
        uploadProgress,

        // Navigation
        navigateToFolder,

        // Folder operations
        createFolder,
        renameFolder,
        moveFolder,
        deleteFolder,
        shareFolder,
        removeFolderShare,

        // File operations
        uploadFile,
        deleteFile,
        openFile,
        moveFile,
        shareFile,
        removeFileShare,

        // Other
        search,
        refetch: currentFolder ? () => fetchFolderContents(currentFolder.id) : fetchRootFolders,
        refetchContents: currentFolder ? () => fetchFolderContents(currentFolder.id) : fetchRootFolders,
    };
};

export default useFileManager;
